import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { Smile, Clock, AlertCircle, Check, CheckCheck } from 'lucide-react';

const ChatBubble = ({ message, isOwn, onReaction }) => {
  const [showReactions, setShowReactions] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);

  const isPending = message._isPending || message.status === 'queued' || message.status === 'sending';
  const isFailed = message.status === 'failed';

  const getEmotionGradient = (emotionTint) => {
    const tints = {
      gold: 'linear-gradient(135deg, #FFE800 0%, #FFE800/50 100%)',
      red: 'linear-gradient(135deg, #FF2A2A 0%, #FF2A2A/50 100%)',
      pink: 'linear-gradient(135deg, #FF66B2 0%, #FF66B2/50 100%)',
      yellow: 'linear-gradient(135deg, #FFE800 0%, #FFE800/50 100%)'
    };
    return tints[emotionTint] || null;
  };

  const handleReactionClick = (emoji, hold = false) => {
    if (isPending || isFailed) return;
    
    if (hold) {
      setIsPulsing(true);
      setTimeout(() => {
        onReaction(message.id, emoji, true);
        setIsPulsing(false);
        setShowReactions(false);
      }, 1000);
    } else {
      onReaction(message.id, emoji, false);
      setShowReactions(false);
    }
  };

  const quickReactions = ['❤️', '😂', '👍', '🎉', '😮', '😢'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`max-w-md ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        {!isOwn && (
          <div className="flex items-center gap-2 px-2">
            {message.sender_avatar ? (
              <img
                src={message.sender_avatar}
                alt={message.sender_name}
                className="w-6 h-6 rounded-full border border-white/10"
              />
            ) : (
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-gradient-to-br from-[#00F0FF]/30 to-[#FFE800]/30 text-white border border-white/10">
                {message.sender_name?.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-xs font-medium text-gray-500">
              {message.sender_name}
            </span>
          </div>
        )}

        <div className="relative group">
          <div
            data-testid={`message-bubble-${message.id}`}
            className={`rounded-2xl px-4 py-3 ${
              isOwn ? 'rounded-tr-sm' : 'rounded-tl-sm'
            } ${isPulsing ? 'animate-pulse' : ''} ${isPending ? 'opacity-60' : ''} ${isFailed ? 'border border-[#FF2A2A]/50' : ''}`}
            style={{
              background: isFailed 
                ? 'rgba(255, 42, 42, 0.1)'
                : message.emotion_tint
                  ? getEmotionGradient(message.emotion_tint)
                  : isOwn
                    ? isPending ? '#565666' : '#00F0FF'
                    : '#0F0F16',
              color: isFailed ? '#FF2A2A' : isOwn ? '#000000' : '#FFFFFF',
              border: !isFailed && !isOwn ? '1px solid rgba(255,255,255,0.05)' : undefined
            }}
            onMouseEnter={() => setShowReactions(true)}
            onMouseLeave={() => !isPulsing && setShowReactions(false)}
          >
            {message.message_type === 'text' && <p className="break-words">{message.content}</p>}

            {message.message_type === 'image' && (
              <div className="space-y-2">
                {message.content && message.content !== '[Image]' && (
                  <p className="break-words">{message.content}</p>
                )}
                <img
                  src={message.media_url}
                  alt="Shared"
                  className="rounded-xl max-w-full"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            )}

            {message.message_type === 'doodle' && (
              <div className="space-y-2">
                {message.content && message.content !== '[Doodle]' && (
                  <p className="break-words">{message.content}</p>
                )}
                <div
                  className="rounded-xl border-2 border-dashed p-2"
                  style={{ borderColor: 'rgba(0,0,0,0.1)', background: 'white' }}
                >
                  <img
                    src={message.doodle_data}
                    alt="Doodle"
                    className="rounded-lg max-w-full"
                  />
                </div>
              </div>
            )}

            {message.reactions && message.reactions.length > 0 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {message.reactions.reduce((acc, reaction) => {
                  const existing = acc.find((r) => r.emoji === reaction.emoji);
                  if (existing) {
                    existing.count++;
                  } else {
                    acc.push({ emoji: reaction.emoji, count: 1 });
                  }
                  return acc;
                }, []).map((r, idx) => (
                  <div
                    key={idx}
                    className="px-2 py-1 rounded-full text-xs flex items-center gap-1"
                    style={{ background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(0,0,0,0.1)' }}
                  >
                    <span>{r.emoji}</span>
                    <span style={{ color: '#6B7280' }}>{r.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {showReactions && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={`absolute ${isOwn ? 'right-0' : 'left-0'} -bottom-12 bg-white rounded-full shadow-lg p-2 flex gap-2 z-10`}
              style={{ border: '1px solid rgba(0,0,0,0.05)' }}
            >
              {quickReactions.map((emoji) => (
                <button
                  key={emoji}
                  data-testid={`reaction-${emoji}-button`}
                  onClick={() => handleReactionClick(emoji)}
                  onMouseDown={() => handleReactionClick(emoji, true)}
                  className="text-xl hover:scale-125 active:scale-150 transition-transform"
                  title="Click for normal reaction, hold for pulse reaction"
                >
                  {emoji}
                </button>
              ))}
            </motion.div>
          )}
        </div>

        <span className="text-xs px-2 flex items-center gap-2" style={{ color: '#9CA3AF' }}>
          {isPending ? (
            <>
              <Clock className="w-3 h-3 animate-pulse" />
              <span>Sending...</span>
            </>
          ) : isFailed ? (
            <>
              <AlertCircle className="w-3 h-3 text-[#FF2A2A]" />
              <span className="text-[#FF2A2A]">Failed to send</span>
            </>
          ) : (
            <>
              {formatDistanceToNow(new Date(message.created_at), { addSuffix: true }).replace('about ', '')}
              {isOwn && message.read_by && message.read_by.length > 1 && (
                <span className="text-xs flex items-center gap-1 text-[#00F0FF]">
                  <CheckCheck className="w-3 h-3" />
                  Read
                </span>
              )}
            </>
          )}
        </span>
      </div>
    </motion.div>
  );
};

export default ChatBubble;